import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import {
  dominant,
  FA_COLOR,
  STAGE_BG,
  vectorAt,
  type EmotionKey,
} from '@/components/researcher/liveDemo/constants'
import { detectFaces, loadFaceModels } from '@/lib/faceApi'
import { primeFirstFrame } from '@/utils/primeFirstFrame'
import { fitWidthPct, STAGE_ASPECT, stageMaxWidth } from './stageFrame'

/** Narrow screens let the frame follow a portrait recording down to this ratio
 *  — on a phone the 16:9 frame is too short for the enlarged face to beat the
 *  pip it was promoted from. */
const NARROW_MIN_FRAME_ASPECT = 3 / 4

/** Displayed-pixel face box (already mapped onto the stage, mirror included). */
type FaceBox = { left: number; top: number; width: number; height: number }

/**
 * Full-size view of the respondent's face recording — the FaceTime-style
 * counterpart of `FacePip`. Same clock contract: the recording covers the whole
 * study, the playhead (`currentMs`) is stimulus-local, so the video tracks
 * `offsetMs + currentMs` and only seeks when it drifts.
 *
 * Emotion indication, in order of fidelity:
 *  1. A face rectangle (client-side tiny face detector, models already shipped
 *     in /public/models) tinted with the dominant emotion's color, with an
 *     emotion chip pinned to it.
 *  2. When detection is unavailable (models fail, or the video is cross-origin
 *     without CORS and would taint the canvas) — a glow around the recording in
 *     the dominant emotion's color plus a corner badge.
 *
 * The recording never sizes the stage: it is drawn into the shared player
 * frame (16:9, same box the stimulus occupies — see `stageFrame`) and centred
 * inside it, so a 4:3 / square / portrait capture is pillarboxed rather than
 * towering over the page. Only on a narrow screen, where that frame is too
 * short for the promotion to be worth anything, does the frame follow the
 * recording's own shape (down to 3:4, capped by the viewport height budget).
 *
 * The video first mounts with `crossOrigin="anonymous"` so detection can read
 * its pixels; if that source rejects CORS (e.g. an S3 redirect without bucket
 * CORS) it remounts plain — playback always wins over the rectangle.
 */
export default function FaceStage({
  src,
  currentMs,
  offsetMs = 0,
  playing,
  series,
  gridMs,
  labels,
  overlay,
  onTogglePlay,
  initialAspect,
  frameAspect,
  frameWidth,
  onAspect,
}: {
  src: string | null
  currentMs: number
  offsetMs?: number
  playing: boolean
  /** Seed for the recording's aspect ratio before the video reports its own
   *  metadata (e.g. handed down by a parent that already measured it via the
   *  pip), so it is fitted to the frame correctly from the first paint. */
  initialAspect?: number
  /** Per-emotion values 0..1 sampled on `gridMs`; drives colors and captions. */
  series?: Record<EmotionKey, (number | null)[]> | null
  /** Grid timestamps in ms, ascending. */
  gridMs?: number[]
  /** Display label per emotion (i18n / locale-specific). */
  labels?: Record<EmotionKey, string>
  /** Shape of the frame the recording is fitted into (default: the shared 16:9
   *  player frame). Pass the recording's own ratio to make it fill the frame. */
  frameAspect?: number
  /** CSS width of the frame, when it must not span its parent (the split layout
   *  gives the face the share of the row its ratio is worth). Everything here is
   *  sized from width — a percentage height would have nothing definite to
   *  resolve against inside an `aspect-ratio` box. */
  frameWidth?: string
  /** Reports the recording's natural aspect ratio (w/h) once metadata loads —
   *  the parent may not have measured it via the pip. */
  onAspect?: (aspect: number) => void
  /** Rendered on top of the stage (e.g. the stimulus picture-in-picture). */
  overlay?: React.ReactNode
  /** Click-to-play: clicking the stage (or its centre play button) toggles playback. */
  onTogglePlay?: () => void
}) {
  const stageRef = useRef<HTMLDivElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  // First attempt loads with crossOrigin="anonymous" (enables pixel reads for
  // detection); a CORS rejection falls back to a plain element, detection off.
  const [cors, setCors] = useState(true)
  const [failed, setFailed] = useState(false)
  const [ready, setReady] = useState(false)
  const [modelsReady, setModelsReady] = useState(false)
  const [detectBroken, setDetectBroken] = useState(false)
  const [box, setBox] = useState<FaceBox | null>(null)
  // The recording's true aspect ratio (portrait phone captures included), which
  // shapes the media box inside the frame — not the frame itself. Defaults to
  // 16:9 until the video reports its intrinsic size, matching FacePip.
  const [aspect, setAspect] = useState(initialAspect ?? STAGE_ASPECT)
  // Latest `playing` for the async first-frame kick (see prime()).
  const playingRef = useRef(playing)
  playingRef.current = playing
  // The first-frame paint runs once per source (and per CORS remount).
  const primedRef = useRef(false)

  // A new source gets a fresh chance to load (and a fresh CORS attempt).
  useEffect(() => {
    setCors(true)
    setFailed(false)
    setReady(false)
    setDetectBroken(false)
    setBox(null)
    primedRef.current = false
  }, [src])

  // Face detector weights load once per app session (cached in lib/faceApi).
  useEffect(() => {
    let cancelled = false
    loadFaceModels()
      .then(() => {
        if (!cancelled) setModelsReady(true)
      })
      .catch(() => {
        if (!cancelled) setDetectBroken(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Reflect play/pause onto the <video>.
  useEffect(() => {
    const v = videoRef.current
    if (!v || !src || failed) return
    if (playing) v.play().catch(() => {})
    else v.pause()
  }, [playing, src, failed, cors])

  // Keep the face video aligned with the playhead (seek on drift). Held in a
  // ref so the media-load callbacks below always seek to the freshest target.
  const targetSecRef = useRef(0)
  targetSecRef.current = (offsetMs + currentMs) / 1000
  // `force` (initial load) bypasses the drift guard and nudges past 0 so the
  // first frame is fetched + decoded even when the playhead sits at the start;
  // otherwise a metadata-only `preload="auto"` leaves the stage black with the
  // spinner stuck on (loadeddata never fires) until the user hits play.
  const applySeek = useCallback((force = false) => {
    const v = videoRef.current
    if (!v) return
    const target = targetSecRef.current
    const clamped = Number.isFinite(v.duration) ? Math.min(target, v.duration || target) : target
    const goal = force ? Math.max(clamped, 0.001) : clamped
    if (force || Math.abs(v.currentTime - clamped) > 0.3) {
      try {
        v.currentTime = goal
      } catch {
        /* not seekable yet — onLoadedMetadata/onLoadedData will retry */
      }
    }
  }, [])
  useEffect(() => {
    if (src && !failed) applySeek()
  }, [currentMs, offsetMs, src, failed, applySeek])

  // Paint the first frame once the recording loads. The force-seek above paints
  // it on desktop, but a paused <video> on mobile won't decode a frame from a
  // programmatic seek without a gesture — leaving the stage black and the spinner
  // stuck (loadeddata never fires) until the user hits play. primeFirstFrame adds
  // a muted play→pause kick that mobile's autoplay policy permits, forcing the
  // decode + paint (and firing loadeddata, which clears the spinner). Skipped
  // while already playing — the play/pause effect paints it then.
  const prime = useCallback(() => {
    const v = videoRef.current
    if (!v || primedRef.current) return
    primedRef.current = true
    if (playingRef.current) {
      applySeek(true)
      return
    }
    void primeFirstFrame(v, targetSecRef.current, () => !playingRef.current)
  }, [applySeek])

  // Detection loop: ~4 Hz tiny-detector pass over the playing video, mapped
  // onto the stage (objectFit: contain + horizontal mirror). The rectangle div
  // below transitions between updates, which smooths the jitter out.
  useEffect(() => {
    if (!ready || !cors || !modelsReady || detectBroken || !src) return
    let stopped = false
    let busy = false
    let misses = 0
    const id = window.setInterval(async () => {
      const v = videoRef.current
      const stage = stageRef.current
      if (!v || !stage || busy || v.readyState < 2) return
      busy = true
      try {
        const detections = await detectFaces(v)
        if (stopped) return
        const det = detections[0]
        if (!det) {
          // Hold the last box through brief detector dropouts before clearing.
          misses += 1
          if (misses >= 3) setBox(null)
          return
        }
        misses = 0
        const cw = stage.clientWidth
        const ch = stage.clientHeight
        const vw = v.videoWidth
        const vh = v.videoHeight
        if (!cw || !ch || !vw || !vh) return
        const scale = Math.min(cw / vw, ch / vh)
        const ox = (cw - vw * scale) / 2
        const oy = (ch - vh * scale) / 2
        // Pad the raw detector box a touch so the frame sits around the face
        // rather than on it.
        const pad = 0.08
        const bx = Math.max(0, det.box.x - det.box.width * pad)
        const by = Math.max(0, det.box.y - det.box.height * pad)
        const bw = Math.min(vw - bx, det.box.width * (1 + 2 * pad))
        const bh = Math.min(vh - by, det.box.height * (1 + 2 * pad))
        setBox({
          // The video renders mirrored (scaleX(-1)), so flip x accordingly.
          left: cw - ox - (bx + bw) * scale,
          top: oy + by * scale,
          width: bw * scale,
          height: bh * scale,
        })
      } catch {
        // Pixel read rejected (tainted canvas) or detector failure — drop to
        // the glow + badge fallback for this source.
        stopped = true
        setDetectBroken(true)
        setBox(null)
      } finally {
        busy = false
      }
    }, 250)
    return () => {
      stopped = true
      window.clearInterval(id)
    }
  }, [ready, cors, modelsReady, detectBroken, src])

  // Dominant emotion at the current playhead — drives every color on stage.
  const vec = series && gridMs ? vectorAt(series, gridMs, currentMs) : null
  const dom = dominant(vec)
  const color = dom && dom[1] > 0 ? FA_COLOR[dom[0]] : null
  const caption =
    dom && labels && dom[1] > 0
      ? { label: labels[dom[0]], pct: Math.round(dom[1] * 100) }
      : null

  const loading = !failed && (!src || !ready)
  // The chip rides on the rectangle's top edge, flipping inside when the face
  // is too close to the stage top for it to fit above.
  const chipAbove = (box?.top ?? 0) > 34

  // Frame vs media. `frame*` is the box the stage occupies in the layout;
  // `mediaWidth` is how much of it the recording takes once fitted inside
  // (100% when it is the wider of the two, less when it is pillarboxed). The
  // narrow-screen frame is allowed to follow a portrait recording, and its
  // width cap is what keeps that taller frame inside the viewport.
  const frameWide = frameAspect ?? STAGE_ASPECT
  const frameNarrow = Math.min(Math.max(aspect, NARROW_MIN_FRAME_ASPECT), frameWide)
  const mediaWidth = frameWidth
    ? fitWidthPct(aspect, frameWide)
    : { xs: fitWidthPct(aspect, frameNarrow), sm: fitWidthPct(aspect, frameWide) }

  const emotionChip = caption && color && (
    <Box
      sx={{
        px: 1,
        py: 0.45,
        borderRadius: 1,
        bgcolor: 'rgba(11,12,30,.82)',
        backdropFilter: 'blur(6px)',
        boxShadow: '0 4px 12px rgba(0,0,0,.35)',
        display: 'flex',
        alignItems: 'center',
        gap: 0.7,
        whiteSpace: 'nowrap',
      }}
    >
      <Box sx={{ width: 9, height: 9, borderRadius: '2px', background: color, flexShrink: 0 }} />
      <Typography sx={{ color: '#fff', fontSize: 12.5, fontWeight: 700, lineHeight: 1.2 }}>
        {caption.label}{' '}
        <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', opacity: 0.9 }}>
          {caption.pct}%
        </Box>
      </Typography>
    </Box>
  )

  return (
    <Box
      sx={{
        position: 'relative',
        ...(frameWidth
          ? { width: frameWidth, flex: '0 0 auto', aspectRatio: `${frameWide}` }
          : {
              width: '100%',
              mx: 'auto',
              maxWidth: { xs: stageMaxWidth(frameNarrow), sm: stageMaxWidth(frameWide) },
              aspectRatio: { xs: `${frameNarrow}`, sm: `${frameWide}` },
            }),
        bgcolor: STAGE_BG,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'grid',
        placeItems: 'center',
      }}
    >
      {/* The recording and everything anchored to it (tint, face rectangle,
          emotion chip) live in a box of the recording's own shape, centred in
          the frame — so the emotion frame hugs the video instead of the
          pillarbox around it. Also the detection reference box: it matches the
          video exactly, so the contain-fit mapping below is a plain scale. */}
      <Box
        ref={stageRef}
        sx={{
          position: 'relative',
          width: mediaWidth,
          aspectRatio: `${aspect}`,
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        {src && !failed && (
          <Box
            // Remount on the CORS fallback so the element loads cleanly from scratch.
            key={cors ? 'cors' : 'plain'}
            component="video"
            ref={videoRef}
            src={src}
            crossOrigin={cors ? 'anonymous' : undefined}
            muted
            playsInline
            preload="auto"
            onLoadedMetadata={(e) => {
              const el = e.currentTarget as HTMLVideoElement
              if (el.videoWidth > 0 && el.videoHeight > 0) {
                const a = el.videoWidth / el.videoHeight
                setAspect(a)
                onAspect?.(a)
              }
              prime()
            }}
            onLoadedData={() => {
              applySeek(true)
              setReady(true)
            }}
            onCanPlay={() => setReady(true)}
            onError={() => {
              if (cors) {
                // Likely a CORS rejection — retry plain; detection stays off.
                setCors(false)
                setReady(false)
                setDetectBroken(true)
                primedRef.current = false
              } else {
                setFailed(true)
              }
            }}
            sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', transform: 'scaleX(-1)' }}
          />
        )}

        {/* Tint over the recording in the dominant emotion's color — always on,
            and the sole emotion cue when the rectangle is unavailable. */}
        {!loading && !failed && color && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 2,
              boxShadow: `inset 0 0 0 3px ${color}, inset 0 0 56px ${color}55`,
              borderRadius: 2,
              transition: 'box-shadow .35s ease',
            }}
          />
        )}

        {/* Face rectangle + emotion chip riding its top edge. */}
        {!loading && !failed && box && color && (
          <Box
            sx={{
              position: 'absolute',
              left: box.left,
              top: box.top,
              width: box.width,
              height: box.height,
              zIndex: 3,
              pointerEvents: 'none',
              border: `2.5px solid ${color}`,
              borderRadius: '12px',
              boxShadow: `0 0 0 2px ${color}33, 0 0 20px ${color}66`,
              transition: 'all .2s linear',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: -2.5,
                ...(chipAbove ? { bottom: '100%', mb: 0.75 } : { top: 6, left: 6 }),
              }}
            >
              {emotionChip}
            </Box>
          </Box>
        )}

        {/* Corner badge — the emotion read-out when there is no face rectangle. */}
        {!loading && !failed && !box && emotionChip && (
          <Box sx={{ position: 'absolute', left: 12, bottom: 12, zIndex: 3, pointerEvents: 'none' }}>{emotionChip}</Box>
        )}
      </Box>

      {loading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', zIndex: 2 }}>
          <CircularProgress size={28} thickness={4} sx={{ color: 'rgba(255,255,255,.85)' }} />
        </Box>
      )}

      {failed && (
        <Typography sx={{ color: 'rgba(255,255,255,.7)', fontSize: 14, px: 3, textAlign: 'center', zIndex: 2 }}>
          Nie udało się odtworzyć nagrania twarzy.
        </Typography>
      )}

      {overlay}

      {/* Click-to-play affordance, mirroring StimulusStage's behaviour. */}
      {onTogglePlay && !playing && (
        <Box
          onClick={onTogglePlay}
          role="button"
          aria-label="Odtwórz"
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 5,
            display: 'grid',
            placeItems: 'center',
            cursor: 'pointer',
            bgcolor: 'rgba(0,0,0,.18)',
            transition: 'background-color .15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,.28)' },
          }}
        >
          <Box
            sx={{
              width: { xs: 64, md: 76 },
              height: { xs: 64, md: 76 },
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,255,255,.92)',
              boxShadow: '0 6px 22px rgba(0,0,0,.35)',
            }}
          >
            <PlayArrowRoundedIcon sx={{ fontSize: { xs: 40, md: 48 }, color: '#16172e', ml: 0.4 }} />
          </Box>
        </Box>
      )}
    </Box>
  )
}
